import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError, aoPerderSessao, authFetch } from '@/api/http-client';
import { getMeControllerObterQueryKey, meControllerObter } from '@/api/generated/me/me';
import type { MeResponse } from '@/api/generated/model';
import { ehRotaPublica, loginComRetorno, ROTAS } from '@/shared/lib/rotas';

export interface ContextoAuth {
  me: MeResponse | undefined;
  carregando: boolean;
  autenticado: boolean;
  entrarPorEmail: (email: string, senha: string) => Promise<void>;
  entrarPorCrmv: (crmv: string, uf: string, senha: string) => Promise<void>;
  sair: () => void;
  recarregar: () => Promise<void>;
}

export const AuthContext = createContext<ContextoAuth | null>(null);

/**
 * `GET /v1/me` é a ÚNICA fonte de sessão (F2-1).
 *
 * Nada de token em `localStorage`: a sessão é um cookie httpOnly que o
 * JavaScript não lê nem precisa ler. Se o `/me` responde, há sessão; se
 * responde 401, não há. Guardar uma cópia do estado de login em qualquer outro
 * lugar só cria uma segunda verdade para divergir da primeira.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const chaveMe = getMeControllerObterQueryKey();

  const { data, isPending, isError, error } = useQuery({
    queryKey: chaveMe,
    queryFn: ({ signal }) => meControllerObter({ signal }),
    // 401 é resposta legítima de "não logado", não falha transitória: repetir
    // só atrasa a tela de login.
    retry: (tentativas, erro) =>
      erro instanceof ApiError && erro.naoAutenticado ? false : tentativas < 2,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const limparSessao = useCallback(() => {
    queryClient.clear();
  }, [queryClient]);

  // Qualquer 401 vindo de qualquer chamada derruba a sessão local uma vez só.
  useEffect(
    () =>
      aoPerderSessao(() => {
        // Em rota pública o 401 NÃO é sessão expirada — é visitante anônimo, e
        // o `GET /me` do próprio provider responde 401 já na primeira carga.
        // Redirecionar daqui jogaria quem abre a landing direto no login, sem
        // nunca ver a página.
        //
        // ⚠️ E o return vem ANTES do `limparSessao`, de propósito: não há sessão
        // para limpar, e o `clear()` destrói as queries da própria página com
        // `cancel({ silent: true })`. A que ainda estivesse em voo (ex.: o
        // `GET /convites/:token`) nunca resolvia, e a tela de convite ficava
        // presa em "Carregando o convite…" sempre que o `/me` chegava antes.
        if (ehRotaPublica(window.location.pathname)) return;

        limparSessao();
        navigate(loginComRetorno(window.location.pathname + window.location.search), {
          replace: true,
        });
      }),
    [limparSessao, navigate],
  );

  const valor = useMemo<ContextoAuth>(() => {
    const naoAutenticado = isError && error instanceof ApiError && error.naoAutenticado;

    return {
      me: data,
      carregando: isPending,
      autenticado: Boolean(data) && !naoAutenticado,

      entrarPorEmail: async (email, senha) => {
        await authFetch('/sign-in/email', { email, password: senha });
        await queryClient.invalidateQueries({ queryKey: chaveMe });
      },

      entrarPorCrmv: async (crmv, uf, senha) => {
        await authFetch('/sign-in/crmv', { crmv, uf, senha });
        await queryClient.invalidateQueries({ queryKey: chaveMe });
      },

      sair: () => {
        // Não espera a resposta para navegar: se o servidor demorar, o usuário
        // ficaria olhando uma tela logada depois de clicar em "Sair".
        void authFetch('/sign-out').catch(() => undefined);
        limparSessao();
        navigate(ROTAS.login, { replace: true });
      },

      recarregar: async () => {
        await queryClient.invalidateQueries({ queryKey: chaveMe });
      },
    };
  }, [data, isPending, isError, error, queryClient, chaveMe, limparSessao, navigate]);

  return <AuthContext.Provider value={valor}>{children}</AuthContext.Provider>;
}
